import React from 'react';
import { DynamicIcon } from '@core/ui';
import { useMemoryGameStore } from '../../../stores/index.js';
import { resolveThemeColor } from './colorUtils';
import styles from './MemoryCard.module.scss';

function CheckIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
    );
}

export const PlaceholderCard = () => {
    const {
        iconName: placeholderIconName,
        iconSize: placeholderIconSize,
        iconColor: placeholderIconColor,
    } = useMemoryGameStore((state) => state.config?.placeholder) || {};
    const { colorType: cardColorType, customColor: cardCustomColor } =
        useMemoryGameStore((state) => state.config?.cards) || {};

    const resolvedThemeColor = resolveThemeColor(cardColorType, cardCustomColor);

    return (
        <div className={styles.cardContainer}>
            <div className={styles.placeholderCard} aria-hidden="true">
                <DynamicIcon
                    name={placeholderIconName || 'IconDiamond'}
                    size={placeholderIconSize}
                    defaultSize={80}
                    color={placeholderIconColor || resolvedThemeColor}
                    strokeWidth={1.5}
                />
            </div>
        </div>
    );
};

const MemoryCard = ({ card, index, shouldPulse }) => {
    const onFlip = useMemoryGameStore((state) => state.flipCard);
    const {
        backIconName,
        backIconSize,
        iconSize: frontIconSize,
        colorType: cardColorType,
        customColor: cardCustomColor,
    } = useMemoryGameStore((state) => state.config?.cards) || {};

    const revealed = card.isFlipped || card.isMatched;
    const iconName = card.iconName || card.pairId;

    const handleClick = () => {
        if (!revealed) {
            onFlip(card.id);
        }
    };

    const resolvedThemeColor = resolveThemeColor(cardColorType, cardCustomColor);

    const cardFrontStyle = !card.isSpecial
        ? {
              backgroundColor: resolvedThemeColor,
          }
        : {};

    const buttonStyle = shouldPulse
        ? {
              '--pulse-color': resolvedThemeColor,
          }
        : {};

    return (
        <div className={styles.cardContainer}>
            <button
                type="button"
                className={`${styles.cardButton} ${revealed ? styles.flipped : ''} ${shouldPulse ? styles.attentionPulse : ''} anl_memory-game--card-${index + 1}`}
                onClick={handleClick}
                disabled={revealed}
                style={buttonStyle}
                aria-label={`Karte ${index + 1}: ${revealed ? card.title : 'verdeckt'}`}
                aria-pressed={revealed}
            >
                {/* Back face (face-down) */}
                <div className={`${styles.cardFace} ${styles.cardBack}`}>
                    {backIconName ? (
                        <DynamicIcon
                            name={backIconName}
                            size={backIconSize}
                            defaultSize={80}
                            color="rgba(var(--bs-dark-rgb, 33, 37, 41), 0.12)"
                            strokeWidth={1}
                        />
                    ) : (
                        <svg
                            width="44"
                            height="44"
                            viewBox="0 0 24 24"
                            fill="rgba(var(--bs-dark-rgb, 33, 37, 41), 0.1)"
                            stroke="rgba(var(--bs-dark-rgb, 33, 37, 41), 0.12)"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                    )}
                </div>

                {/* Front face (face-up / revealed) */}
                <div
                    className={`${styles.cardFace} ${styles.cardFront} ${card.isSpecial ? styles.specialCard : ''}`}
                    style={cardFrontStyle}
                >
                    <DynamicIcon name={iconName} size={frontIconSize} defaultSize={80} color="white" />
                    {card.isMatched && (
                        <span className={styles.matchedCheck}>
                            <CheckIcon />
                        </span>
                    )}
                </div>
            </button>
        </div>
    );
};

export default MemoryCard;
